import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import TenantBrandLockup from '../brand/TenantBrandLockup';
import {
  attachmentSizeLabel,
  loadActiveProgramFeedAttachments,
  signProgramFeedAttachments,
} from './programFeedAttachments';
import './program-feed.css';

function isRems(profile) {
  return String(profile?.member_kind || 'line_medic').startsWith('rems_');
}

function when(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function priorityLabel(value) {
  if (value === 'urgent') return 'Urgent';
  if (value === 'important') return 'Important';
  return 'Program Update';
}

function PostDetail({ post, profile, read, acknowledged, onChanged, onBack }) {
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadComments = useCallback(async () => {
    if (!post?.comments_enabled) return setComments([]);
    const { data, error: queryError } = await supabase
      .from('program_feed_comments')
      .select('id, body, created_at, member_id')
      .eq('post_id', post.id)
      .is('deleted_at', null)
      .order('created_at', { ascending: true });
    if (queryError) return setError(queryError.message || 'Unable to load comments.');
    setComments(Array.isArray(data) ? data : []);
  }, [post]);

  const loadAttachments = useCallback(async () => {
    if (!post?.id) return setAttachments([]);
    try {
      const rows = await loadActiveProgramFeedAttachments(post.id);
      const signed = await signProgramFeedAttachments(rows);
      setAttachments(signed);
    } catch (attachmentError) {
      setAttachments([]);
      setError(attachmentError?.message || 'Unable to load post attachments.');
    }
  }, [post]);

  useEffect(() => { loadComments(); }, [loadComments]);
  useEffect(() => { loadAttachments(); }, [loadAttachments]);

  useEffect(() => {
    if (!post?.id || read) return;
    supabase.from('program_feed_reads').insert({ post_id: post.id, member_id: profile.id, revision: post.revision }).then(({ error: insertError }) => {
      if (insertError && insertError.code !== '23505') setError(insertError.message || 'Unable to record read status.');
      onChanged?.();
    });
  }, [post, profile, read, onChanged]);

  const acknowledge = async () => {
    setBusy(true); setError('');
    const { error: insertError } = await supabase.from('program_feed_acknowledgments').insert({ post_id: post.id, member_id: profile.id, revision: post.revision });
    setBusy(false);
    if (insertError && insertError.code !== '23505') return setError(insertError.message || 'Unable to acknowledge this update.');
    onChanged?.();
  };

  const submitComment = async event => {
    event.preventDefault();
    const body = comment.trim();
    if (!body) return;
    setBusy(true); setError('');
    const { error: insertError } = await supabase.from('program_feed_comments').insert({ post_id: post.id, member_id: profile.id, body });
    setBusy(false);
    if (insertError) return setError(insertError.message || 'Unable to post comment.');
    setComment('');
    await loadComments();
  };

  return (
    <section className="program-feed-detail">
      <TenantBrandLockup profile={profile} compact />
      <button type="button" className="program-feed-back" onClick={onBack}>← Program Feed</button>
      <article className={`program-feed-post priority-${post.priority || 'normal'}`}>
        <div className="program-feed-post-meta"><span>{priorityLabel(post.priority)}</span><time>{when(post.published_at || post.created_at)}</time></div>
        <h2>{post.title}</h2>
        <div className="program-feed-body">{post.body}</div>
        {attachments.length > 0 && (
          <section className="program-feed-attachments" aria-label="Post attachments">
            <h3>Attachments</h3>
            <div className="program-feed-attachment-grid">
              {attachments.map(item => {
                const image = String(item.mime_type || '').startsWith('image/');
                return (
                  <button
                    type="button"
                    className={`program-feed-attachment-card${image ? ' image' : ' pdf'}`}
                    key={item.id}
                    onClick={() => item.signedUrl && setViewer(item)}
                    disabled={!item.signedUrl}
                  >
                    {image && item.signedUrl
                      ? <img src={item.signedUrl} alt="" />
                      : <div className="program-feed-pdf-mark">PDF</div>}
                    <span>
                      <strong>{item.original_name}</strong>
                      <small>{attachmentSizeLabel(item.size_bytes)}{item.signedError ? ' · Unavailable' : ''}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
        {post.acknowledgment_required && !acknowledged && <div className="program-feed-required"><strong>Acknowledgment required</strong><span>Confirm that you have reviewed this program update.</span><button type="button" disabled={busy} onClick={acknowledge}>{busy ? 'Saving…' : 'Acknowledge'}</button></div>}
        {post.acknowledgment_required && acknowledged && <div className="program-feed-acknowledged">✓ Acknowledged</div>}
      </article>
      {error && <div className="program-feed-error">{error}</div>}
      {post.comments_enabled && <section className="program-feed-comments"><h3>Comments</h3>{comments.length ? comments.map(item => <div className="program-feed-comment" key={item.id}><strong>{item.member_id === profile.id ? 'You' : 'Line Medic member'}</strong><span>{item.body}</span><small>{when(item.created_at)}</small></div>) : <p className="program-feed-muted">No comments yet.</p>}<form onSubmit={submitComment}><textarea value={comment} onChange={event => setComment(event.target.value)} maxLength={4000} placeholder="Add a comment…" /><button type="submit" disabled={busy || !comment.trim()}>Post Comment</button></form></section>}
      {viewer && (
        <div className="program-feed-viewer" role="dialog" aria-modal="true" aria-label={viewer.original_name}>
          <div className="program-feed-viewer-bar">
            <div><strong>{viewer.original_name}</strong><small>{attachmentSizeLabel(viewer.size_bytes)}</small></div>
            <div>
              <button type="button" onClick={() => window.open(viewer.signedUrl, '_blank', 'noopener,noreferrer')}>Open</button>
              <button type="button" onClick={() => setViewer(null)}>Close</button>
            </div>
          </div>
          <div className="program-feed-viewer-content">
            {String(viewer.mime_type || '').startsWith('image/')
              ? <img src={viewer.signedUrl} alt={viewer.original_name} />
              : <iframe title={viewer.original_name} src={viewer.signedUrl} />}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ProgramFeed({ profile, initialPostId = '' }) {
  const [posts, setPosts] = useState([]);
  const [reads, setReads] = useState([]);
  const [acks, setAcks] = useState([]);
  const [selectedId, setSelectedId] = useState(initialPostId);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!profile?.id || isRems(profile)) return;
    setError('');
    const [postResult, readResult, ackResult] = await Promise.all([
      // Member View must use member-scoped targeting even when the signed-in member is
      // also a Program Admin. The RPC intentionally does not inherit the broader admin
      // RLS read bypass, so Specific Members / Currently Deployed snapshots remain true.
      supabase.rpc('member_list_program_feed_posts'),
      supabase.from('program_feed_reads').select('post_id, revision, read_at').eq('member_id', profile.id),
      supabase.from('program_feed_acknowledgments').select('post_id, revision, acknowledged_at').eq('member_id', profile.id),
    ]);
    const firstError = postResult.error || readResult.error || ackResult.error;
    if (firstError) { setError(firstError.message || 'Program Feed is not available yet.'); setStatus('error'); return; }
    setPosts(Array.isArray(postResult.data) ? postResult.data : []);
    setReads(Array.isArray(readResult.data) ? readResult.data : []);
    setAcks(Array.isArray(ackResult.data) ? ackResult.data : []);
    setStatus('ready');
  }, [profile]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedId(initialPostId || ''); }, [initialPostId]);

  const selected = useMemo(() => posts.find(post => String(post.id) === String(selectedId)) || null, [posts, selectedId]);
  const hasRead = post => reads.some(item => item.post_id === post.id && Number(item.revision) === Number(post.revision));
  const hasAck = post => acks.some(item => item.post_id === post.id && Number(item.revision) === Number(post.revision));

  if (isRems(profile)) return <div className="program-feed-denied">Program Feed is not available for REMS access.</div>;

  if (selected) {
    return <PostDetail post={selected} profile={profile} read={hasRead(selected)} acknowledged={hasAck(selected)} onChanged={load} onBack={() => { setSelectedId(''); window.history.replaceState({}, '', '/program-feed'); }} />;
  }

  return (
    <section className="program-feed-root">
      <TenantBrandLockup profile={profile} compact />
      <div className="program-feed-heading"><div><span>PROGRAM COMMUNICATIONS</span><h2>Program Feed</h2><p>Updates, required acknowledgments, and program discussion.</p></div><button type="button" onClick={load}>Refresh</button></div>
      {status === 'loading' && <div className="program-feed-empty">Loading Program Feed…</div>}
      {error && <div className="program-feed-error">{error}</div>}
      {status === 'ready' && !posts.length && <div className="program-feed-empty">No program updates right now.</div>}
      <div className="program-feed-list">
        {posts.map(post => {
          const read = hasRead(post); const acknowledged = hasAck(post); const required = post.acknowledgment_required && !acknowledged;
          return <button type="button" key={post.id} className={`program-feed-row${read ? '' : ' unread'}${required ? ' required' : ''}`} onClick={() => { setSelectedId(post.id); window.history.replaceState({}, '', `/program-feed/${post.id}`); }}><div className="program-feed-row-top"><span>{priorityLabel(post.priority)}</span><time>{when(post.published_at || post.created_at)}</time></div><strong>{post.title}</strong><p>{String(post.body || '').slice(0, 180)}{String(post.body || '').length > 180 ? '…' : ''}</p><div className="program-feed-row-state">{!read && <span>New</span>}{required && <span>Acknowledgment required</span>}{post.comments_enabled && <span>Comments open</span>}</div></button>;
        })}
      </div>
    </section>
  );
}
